class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.minimumSearchTermLength = 2;
    this.requestSequence = 0;
    this.searchTerm = '';
    this.resultsId = this.input.id + '-results';
    this.input.setAttribute('aria-controls', this.resultsId);
    this.suggestionsHeading = this.decodeHtmlEntities(this.getAttribute('data-suggestions-label') || 'Suggestions');
    this.searchForTemplate = this.decodeHtmlEntities(
      this.getAttribute('data-search-for-text') || 'View all results for "[terms]"'
    );
    this.noResultsTemplate = this.decodeHtmlEntities(
      this.getAttribute('data-no-results-text') || 'No results found for "[terms]"'
    );
    this.suggestionsCountTemplate = this.decodeHtmlEntities(
      this.getAttribute('data-suggestions-count-text') || '[count] suggestions'
    );

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.replace(/^\s+|\s+$/g, '');
  }

  onChange() {
    super.onChange();
    var newSearchTerm = this.getQuery();
    if (!this.searchTerm || newSearchTerm.indexOf(this.searchTerm) !== 0) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      var resultsWrapper = this.querySelector('.predictive-search__results-groups-wrapper');
      if (resultsWrapper && resultsWrapper.parentNode) resultsWrapper.parentNode.removeChild(resultsWrapper);
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.close(true);
      return;
    }

    if (this.searchTerm.length < this.minimumSearchTermLength) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.closeResults(false);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (event.defaultPrevented) return;
    if (!window.PS || !window.PS.Search) return;
    event.preventDefault();
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) return;
    window.location.href = this.buildSearchPageUrl(this.getQuery(), 1);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelActiveRequest();
      this.resetResultsState();
      this.closeResults(true);
    }
  }

  onFocus() {
    var currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;
    if (currentSearchTerm.length < this.minimumSearchTermLength) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.closeResults(false);
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(
      function () {
      if (!this.contains(document.activeElement)) this.close();
      }.bind(this)
    );
  }

  onKeyup(event) {
    var query = this.getQuery();

    if (!query.length) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.close(true);
    } else if (query.length < this.minimumSearchTermLength) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.closeResults(false);
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onClick(event) {
    if (this.handleSearchContextClick(event)) return;
    var selectedAction = this.findAncestor(event.target, '[data-ps-search-selection]');
    if (selectedAction && window.PS && window.PS.Search) window.PS.Search.track('selected', this.lastSearchData, 'suggestions', selectedAction.getAttribute('data-ps-search-selection'));
    var focusAction = this.findAncestor(event.target, '[data-ps-search-focus]');
    var helpButton = this.findAncestor(event.target, '[data-predictive-search-help]');
    var opened = false;

    if (focusAction) {
      event.preventDefault();
      this.input.value = '';
      this.onChange();
      this.input.placeholder = 'Enter a paint code, such as Toyota 040';
      this.input.focus();
      return;
    }

    if (!helpButton) return;

    event.preventDefault();
    opened = this.openSaveColorHelp(this.getQuery());
    if (opened) this.close();
  }

  updateSearchForTerm(newTerm) {
    var searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');

    if (searchForTextElement) {
      searchForTextElement.innerText = this.getSensitiveSearchData(newTerm) ? 'Get help finding your paint code' : this.getSearchForText(newTerm);
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    var moveUp = direction === 'up';
    var selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    var allElements = this.querySelectorAll('li, button.predictive-search__item');
    var allVisibleElements = [];
    var elementIndex;
    var activeElementIndex = 0;

    for (elementIndex = 0; elementIndex < allElements.length; elementIndex++) {
      if (allElements[elementIndex].offsetParent !== null) {
        allVisibleElements.push(allElements[elementIndex]);
      }
    }

    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    var selectedElementIndex = -1;
    var i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    var activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    var selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (!window.PS || !window.PS.Search) { this.cancelActiveRequest(); this.close(); return; }
    var sensitive = this.getSensitiveSearchData(searchTerm);
    var queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    var context = this.getSearchContext(searchTerm);
    queryKey += ':' + JSON.stringify(context);
    var requestSequence;

    if (sensitive) {
      window.PS.Search.track('eligible', sensitive, 'suggestions');
      this.cancelActiveRequest();
      this.renderSearchResults(this.buildPredictiveSearchMarkup('', sensitive));
      return;
    }

    if (!searchTerm || searchTerm.length < this.minimumSearchTermLength) {
      this.cancelActiveRequest();
      this.resetResultsState();
      this.closeResults(false);
      return;
    }

    this.cancelActiveRequest();
    requestSequence = this.requestSequence;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.buildPredictiveSearchMarkup(searchTerm, this.cachedResults[queryKey]));
      return;
    }

    this.requestPredictiveSearchData(searchTerm, requestSequence, this.abortController.signal)
      .then(function(data) { return requestSequence === this.requestSequence ? this.addContextActions(data, searchTerm, context) : data; }.bind(this))
      .then(
        function (searchData) {
          if (requestSequence !== this.requestSequence || searchTerm !== this.searchTerm) {
            return;
          }
          var resultsMarkup = this.buildPredictiveSearchMarkup(searchTerm, searchData);
          this.cachedResults[queryKey] = searchData;
          this.renderSearchResults(resultsMarkup);
        }.bind(this)
      )
      .catch(
        function (error) {
          if (this.isAbortError(error)) {
            return;
          }
          if (requestSequence !== this.requestSequence) {
            return;
          }
          this.renderSearchResults(this.buildPredictiveSearchMarkup(searchTerm, { status: 'unavailable', suggestions: [] }));
        }.bind(this)
      );
  }

  buildResultsRequestUrl(searchTerm, options) {
    var params = [];
    options = options || {};

    function pushParam(key, value) {
      if (!value && value !== 0) return;
      value = String(value).replace(/^\s+|\s+$/g, '');
      if (!value) return;
      params.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }

    pushParam('q', searchTerm);
    pushParam('page', 1);
    pushParam('filter_by', options.filterBy);
    pushParam('sort_by', options.sortBy);
    pushParam('per_page', options.perPage);
    pushParam('predictive', options.predictive ? 1 : '');
    var scope = this.getSearchContext(searchTerm);
    pushParam('make', scope.make);
    pushParam('year', scope.year);
    pushParam('model', scope.model);

    return this.getWorkerSearchUrl('results') + '?' + params.join('&');
  }

  getPredictiveSuggestions(data) {
    if (data && data.suggestions && data.suggestions.slice) {
      return data.suggestions.slice(0, 6);
    }

    if (data && data.results && data.results.slice) {
      return data.results.slice(0, 6);
    }

    return null;
  }

  requestPredictiveSearchData(searchTerm, requestSequence, signal) {
    var predictiveUrl = this.buildResultsRequestUrl(searchTerm, {
      predictive: true,
      perPage: 6,
    });

    return this.requestJson(predictiveUrl, { signal: signal })
      .then(
        function (data) {
          var suggestions;

          if (requestSequence !== this.requestSequence) {
            return {
              query: searchTerm,
              suggestions: [],
            };
          }

          suggestions = this.getPredictiveSuggestions(data);
          if (suggestions) {
            data.suggestions = suggestions;
            return data;
          }

          return this.requestPredictiveSearchFallback(searchTerm, signal);
        }.bind(this)
      );
  }

  requestPredictiveSearchFallback(searchTerm, signal) {
    var resultsUrl = this.buildResultsRequestUrl(searchTerm, { perPage: 6 });

    return this.requestJson(resultsUrl, { signal: signal }).then(function (data) {
      var fallbackResults = data && data.results ? data.results.slice(0, 6) : [];

      data.suggestions = fallbackResults;
      return data;
    });
  }

  cancelActiveRequest() {
    this.requestSequence += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  resetResultsState() {
    this.removeAttribute('results');
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  buildPredictiveSearchMarkup(searchTerm, searchData) {
    this.lastSearchData = searchData;
    var suggestions = searchData && searchData.suggestions ? searchData.suggestions : [];
    var actions = searchData && searchData.actions ? searchData.actions : [];
    var guidance = this.getSearchGuidanceText(searchData);
    var isVinRecovery = searchData && searchData.intent && searchData.intent.type === 'vin_help';
    var isNoResults = searchData && !searchData.query_redacted && !isVinRecovery &&
      (searchData.status === 'no_results' || (searchData.status === 'ok' && searchData.totalFound === 0)) &&
      suggestions.length === 0;
    var markup = this.buildSearchContextMarkup(searchData && searchData.context) + '<div id="' + this.escapeHtml(this.resultsId) + '" role="listbox" aria-label="Search suggestions">';
    var suggestionIndex;
    var actionIndex;
    var actionClass;

    if (searchData && searchData.status === 'unavailable') {
      guidance = 'Search is unavailable right now. Try again, or find your paint code below.';
      actions = [{id:'find_paint_code',kind:'link',label:'Find my paint code',url:'/touch-up-paint-codes/paint-code.html'}];
    }
    if (isNoResults) {
      guidance = 'Try a paint code, vehicle, or product name.';
      actions = [
        {id:'find_paint_code',kind:'link',label:'Find my paint code',url:'/touch-up-paint-codes/paint-code.html'},
        {id:'choose_vehicle',kind:'vehicle_selector',label:'Choose my vehicle',url:'/'},
        {id:'label_photo',kind:'link',label:'Upload label photo',url:'/pages/help-me-find-my-color?photo=paint_label#ps-paint-label-help'}
      ];
      markup += '<h2 class="predictive-search__heading ps-search-no-results" role="presentation">No matches for "' + this.escapeHtml(searchTerm) + '"</h2>';
    }
    if (guidance) markup += '<p class="predictive-search__heading text-body" role="presentation">' + this.escapeHtml(guidance) + '</p>';
    if (actions.length) {
      markup += '<ul class="predictive-search__results-list list-unstyled' + (isVinRecovery || isNoResults ? ' ps-search-actions' : '') + '" role="group" aria-label="Next steps">';
      for (actionIndex = 0; actionIndex < actions.length; actionIndex++) {
        actionClass = isVinRecovery || isNoResults ? (actionIndex === 0 ? ' ps-search-action--primary' : ' ps-search-action--secondary') : '';
        markup += '<li id="' + this.escapeHtml(this.resultsId) + '-action-' + actionIndex + '" role="option" aria-selected="false">' +
          '<a class="predictive-search__item link link--text' + actionClass + '" tabindex="-1" href="' + this.escapeHtml(this.getSearchActionUrl(actions[actionIndex])) + '"' +
          ' data-ps-search-selection="' + this.escapeHtml(actions[actionIndex].id) + '"' +
          (actions[actionIndex].kind === 'focus_search' ? ' data-ps-search-focus' : '') + '>' + this.escapeHtml(actions[actionIndex].label) +
          (isVinRecovery || isNoResults ? '<span class="ps-search-action-arrow" aria-hidden="true"></span>' : '') + '</a></li>';
      }
      markup += '</ul>';
    }

    if (suggestions.length) {
      markup +=
        '<div class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products">';
      markup += '<div class="predictive-search__result-group"><div>';
      markup +=
        '<h2 id="' + this.escapeHtml(this.resultsId) + '-heading" class="predictive-search__heading text-body caption-with-letter-spacing">' +
        this.escapeHtml(this.suggestionsHeading) +
        '</h2>';
      markup +=
        '<ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="' + this.escapeHtml(this.resultsId) + '-heading">';

      for (suggestionIndex = 0; suggestionIndex < suggestions.length; suggestionIndex++) {
        markup += this.buildSuggestionMarkup(suggestions[suggestionIndex], suggestionIndex + 1);
      }

      markup += '</ul></div></div></div>';
    }

    if ((!searchData || !searchData.query_redacted) && !isNoResults) markup +=
      '<div class="predictive-search__search-for-button">' +
      '<button id="' + this.escapeHtml(this.resultsId) + '-all" class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      '<span data-predictive-search-search-for-text>' +
      this.escapeHtml(this.getSearchForText(searchTerm)) +
      '</span>' +
      '<span class="svg-wrapper">' +
      '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>' +
      '</span>' +
      '</button>' +
      '</div>';

    markup += '</div>';
    markup +=
      '<span class="hidden" data-predictive-search-live-region-count-value>' +
      this.escapeHtml(isNoResults ? 'No matches for "' + searchTerm + '". ' + guidance : (guidance || this.getLiveRegionTextForResults(searchTerm, suggestions.length))) +
      '</span>';

    return markup;
  }

  splitSuggestionColorTitle(title) {
    var raw = String(title || '').replace(/\s+Touch Up Paint\s*$/i, '').replace(/^\s+|\s+$/g, '');
    var match = raw.match(/\s*\(([^)]+)\)\s*$/);

    if (match && match[1]) {
      return {
        name: raw.substring(0, match.index).replace(/\s+$/g, ''),
        code: String(match[1] || '').replace(/^\s+|\s+$/g, '')
      };
    }

    return {
      name: raw,
      code: ''
    };
  }

  getSuggestionColorData(suggestion) {
    var rawTitle = suggestion && suggestion.title ? String(suggestion.title) : '';
    var parsed = this.splitSuggestionColorTitle(rawTitle);
    var make = suggestion && suggestion.make ? String(suggestion.make).replace(/^\s+|\s+$/g, '') : '';
    var code = suggestion && suggestion.color_code ? String(suggestion.color_code).replace(/^\s+|\s+$/g, '') : '';
    var name = suggestion && suggestion.color_name ? String(suggestion.color_name).replace(/^\s+|\s+$/g, '') : parsed.name;
    var isColor = !!(suggestion && suggestion.type === 'color_hub');

    if (!isColor) return null;

    if (make && name.toLowerCase().indexOf(make.toLowerCase() + ' ') === 0) {
      name = name.substring(make.length).replace(/^\s+/, '');
    }

    return {
      make: make,
      name: name || 'Color name unavailable',
      code: code ? code.toUpperCase() : 'Code unavailable'
    };
  }

  formatSuggestionCode(code) {
    return this.escapeHtml(code).replace(/([\/\-,.])/g, '$1<wbr>').replace(/\(/g, '<wbr>(');
  }

  buildSuggestionMarkup(suggestion, index) {
    var suggestionTitle = suggestion && suggestion.title ? suggestion.title : '';
    var suggestionUrl = suggestion && suggestion.url ? this.normalizePaintscratchUrl(suggestion.url) : '#';
    var colorData = this.getSuggestionColorData(suggestion);
    var colorRgb = this.normalizeColorRgb(suggestion && suggestion.color_rgb ? suggestion.color_rgb : '');
    var swatchMarkup = colorRgb
      ? '<span class="predictive-search__color-swatch" style="background-color: ' +
        this.escapeHtml(colorRgb) +
        ';" aria-hidden="true"></span>'
      : '';
    var contentClass = swatchMarkup
      ? 'predictive-search__item-content predictive-search__item-content--centered predictive-search__item-content--with-swatch'
      : 'predictive-search__item-content predictive-search__item-content--centered';
    var labelText = colorData
      ? 'Select color code ' + colorData.code + ', ' + (colorData.make ? colorData.make + ' ' : '') + colorData.name + '.'
      : suggestionTitle;
    var makeSuffix = colorData && colorData.make ? ' for ' + this.escapeHtml(colorData.make) : '';
    var headingMarkup = colorData
      ? '<span class="predictive-search__color-code">' +
        this.formatSuggestionCode(colorData.code) + ' - ' + this.escapeHtml(colorData.name) + makeSuffix +
        '</span>'
      : this.escapeHtml(suggestionTitle);

    return (
      '<li id="' + this.escapeHtml(this.resultsId) + '-query-' +
      index +
      '" class="predictive-search__list-item" role="option" aria-selected="false">' +
      '<a href="' +
      this.escapeHtml(suggestionUrl) +
      '" class="predictive-search__item link link--text" data-ps-search-selection="' + (colorData ? 'color' : suggestion.type === 'shopify_product' ? 'product' : 'help') + '" tabindex="-1">' +
      '<div class="' +
      contentClass +
      '">' +
      swatchMarkup +
      '<p class="predictive-search__item-heading predictive-search__item-query-result h5" aria-label="' +
      this.escapeHtml(labelText) +
      '">' +
      headingMarkup +
      '</p>' +
      '</div>' +
      '</a>' +
      '</li>'
    );
  }

  hasColorRelatedSuggestionContext(suggestions) {
    var suggestionIndex;
    var suggestion;
    var suggestionTitle = '';
    var suggestionUrl = '';
    var combined = '';

    if (!suggestions || !suggestions.length) return false;

    for (suggestionIndex = 0; suggestionIndex < suggestions.length; suggestionIndex++) {
      suggestion = suggestions[suggestionIndex] || {};
      suggestionTitle = String(suggestion.title || '').replace(/^\s+|\s+$/g, '').toLowerCase();
      suggestionUrl = String(suggestion.url || '').replace(/^\s+|\s+$/g, '').toLowerCase();
      combined = suggestionTitle + ' ' + suggestionUrl;

      if (!combined.replace(/^\s+|\s+$/g, '')) continue;
      if (suggestionUrl.indexOf('/colors/') !== -1) return true;
      if (suggestion.color_rgb) return true;
      if (
        combined.indexOf('factory code') !== -1 ||
        combined.indexOf('paint code') !== -1 ||
        combined.indexOf('missing color request') !== -1 ||
        combined.indexOf('color hub') !== -1 ||
        combined.indexOf('touch up paint codes') !== -1 ||
        combined.indexOf('color request') !== -1
      ) {
        return true;
      }
    }

    return false;
  }

  getSaveColorHelpContext(searchTerm) {
    var cc = {};
    var pathname = '';
    var rawParts = [];
    var parts = [];
    var partIndex;
    var makeFromPath = '';
    var queryString = '';
    var queryPairs = [];
    var queryIndex;
    var queryPair = [];
    var queryKey = '';
    var queryValue = '';
    var queryValues = {};

    try {
      if (window.PS && PS.cc && typeof PS.cc.read === 'function') {
        cc = PS.cc.read() || {};
      }
    } catch (_e) {
      cc = {};
    }

    pathname = window.location && window.location.pathname ? String(window.location.pathname) : '';
    rawParts = pathname.split('/');
    for (partIndex = 0; partIndex < rawParts.length; partIndex++) {
      if (rawParts[partIndex]) parts.push(rawParts[partIndex]);
    }
    if (parts.length > 1) {
      if (parts[0] === 'touch_up_paint') makeFromPath = decodeURIComponent(parts[1]);
      if (parts[0] === 'colors') makeFromPath = decodeURIComponent(String(parts[1] || '').replace(/-/g, ' '));
    }

    queryString = window.location && window.location.search ? String(window.location.search) : '';
    if (queryString && queryString.charAt(0) === '?') queryString = queryString.substring(1);
    queryPairs = queryString ? queryString.split('&') : [];
    for (queryIndex = 0; queryIndex < queryPairs.length; queryIndex++) {
      if (!queryPairs[queryIndex]) continue;
      queryPair = queryPairs[queryIndex].split('=');
      queryKey = decodeURIComponent(String(queryPair[0] || '').replace(/\+/g, ' ')).toLowerCase();
      queryValue = decodeURIComponent(String(queryPair[1] || '').replace(/\+/g, ' '));
      if (!queryKey) continue;
      queryValues[queryKey] = queryValue;
    }

    return {
      source: 'manual',
      source_context: 'header_search',
      trigger_type: 'help_click',
      source_page_url: window.location ? window.location.origin + window.location.pathname : '',
      search_term: this.getSensitiveSearchData(searchTerm) ? '' : (searchTerm || ''),
      make: makeFromPath || queryValues.make || cc.make || '',
      year: queryValues.year || cc.year || '',
      model: queryValues.model || cc.model || '',
      candidate_color: '',
      candidate_code: ''
    };
  }

  openSaveColorHelp(searchTerm) {
    var context = this.getSaveColorHelpContext(searchTerm);

    if (!window.PS || !PS.SaveColor) return false;
    if (typeof PS.SaveColor.openHelp === 'function') return PS.SaveColor.openHelp(context);
    if (typeof PS.SaveColor.open === 'function') return PS.SaveColor.open(context);

    return false;
  }

  getSearchForText(searchTerm) {
    return this.formatText(this.searchForTemplate, { terms: searchTerm });
  }

  getNoResultsText(searchTerm) {
    return this.formatText(this.noResultsTemplate, { terms: searchTerm });
  }

  getLiveRegionTextForResults(searchTerm, suggestionCount) {
    if (!suggestionCount) {
      return this.getNoResultsText(searchTerm);
    }

    return this.formatText(this.suggestionsCountTemplate, { count: suggestionCount });
  }

  isAbortError(error) {
    return error && (error.code === 20 || error.name === 'AbortError');
  }

  setLiveRegionLoadingState() {
    this.input.setAttribute('aria-busy', 'true');
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(
      function () {
      this.statusElement.setAttribute('aria-hidden', 'true');
      }.bind(this),
      1000
    );
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    if (window.PS && window.PS.Search) window.PS.Search.track('shown', this.lastSearchData, 'suggestions');
  }

  setLiveRegionResults() {
    this.input.setAttribute('aria-busy', 'false');
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    var sectionHeader = document.querySelector('.section-header');
    var sectionHeaderBottom = sectionHeader ? sectionHeader.getBoundingClientRect().bottom : 0;

    this.resultsMaxHeight = window.innerHeight - sectionHeaderBottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight() + 'px';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm) {
    this.input.setAttribute('aria-busy', 'false');
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    var selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
